/**
 * Who a Send window emails (web and app): the store's contacts as one
 * compact row each, a search over them when the list is long, and the
 * recipients list the send carries. Pure data in, data out, so both
 * platforms list, filter and send the same way and it tests without either.
 */
import { contactRoleNames, INVOICE_CONTACT_ROLE_LABELS } from './invoicePeople';
/**
 * Contact lists this long or longer get a search field. A store normally has
 * about one contact per built-in role (Main, Accounting, Service manager,
 * Sales manager, Other: five), which fits on a phone without searching; one
 * more than that is a list worth searching.
 */
export const SEND_CONTACT_SEARCH_MIN = Object.keys(INVOICE_CONTACT_ROLE_LABELS).length + 1;
/** Whether the contacts get a search field: a long list, or always when it also finds customers. */
export function shouldOfferContactSearch(contactCount, findsCustomers = false) {
    return findsCustomers || contactCount >= SEND_CONTACT_SEARCH_MIN;
}
// Only trouble earns words; "ok" (delivering) says nothing.
const STATUS = {
    spam: { label: 'Marked as spam: won’t be emailed', tone: 'danger', skipped: true },
    blocked: { label: 'Blocked: won’t be emailed', tone: 'danger', skipped: true },
    bouncing: { label: 'Bouncing', tone: 'danger', skipped: false },
    delayed: { label: 'Delayed', tone: 'caution', skipped: false },
    unknown: { label: 'No delivery report yet', tone: 'caution', skipped: false },
    test: { label: 'Test address', tone: 'info', skipped: false },
};
/** The status words for a contact's delivery health, or null when there's nothing to say. */
export function sendContactStatus(health) {
    return (health && STATUS[health.status]) || null;
}
export function sendContactRow(c, roleLabel) {
    const roles = contactRoleNames(c, roleLabel).join(', ');
    const status = sendContactStatus(c.health);
    return {
        title: c.name || c.email,
        email: c.name ? c.email : '',
        roles,
        status,
        spoken: [c.name, c.email, roles, status?.label].filter(Boolean).join(', '),
    };
}
const words = (query) => query.trim().toLowerCase().split(/\s+/).filter(Boolean);
/** Whether a contact matches every typed word, in its name, address or role names (any order and case). */
export function matchesSendContact(c, query, roleLabel) {
    const typed = words(query);
    if (typed.length === 0)
        return true;
    const text = [c.name ?? '', c.email, ...contactRoleNames(c, roleLabel)].join(' ').toLowerCase();
    return typed.every((w) => text.includes(w));
}
/**
 * The contacts to show for a search. With nothing typed, all of them in their
 * order. With a search, the `pinned` ones first (those ticked when the search
 * changed: a choice is never hidden by a search) and then the other matches.
 * Pinning is a snapshot rather than the live ticks, so a match ticked while
 * searching stays where it is under the finger.
 */
export function arrangeSendContacts(contacts, query, pinned, roleLabel) {
    if (words(query).length === 0)
        return { rows: [...contacts], noMatch: false };
    const pin = new Set(pinned.map((e) => e.trim().toLowerCase()));
    const isPinned = (c) => pin.has(c.email.trim().toLowerCase());
    const matches = contacts.filter((c) => matchesSendContact(c, query, roleLabel));
    return {
        rows: [...contacts.filter(isPinned), ...matches.filter((c) => !isPinned(c))],
        noMatch: matches.length === 0,
    };
}
/** The line shown when nothing matches the search. */
export function noContactMatchText(query, findsCustomers = false) {
    const q = query.trim();
    return findsCustomers ? `No contact or customer matches “${q}”` : `No contact matches “${q}”`;
}
/**
 * Every address the send goes to, each once: the same address in another
 * case is the same person. Keeps the first spelling and the lists' order.
 */
export function mergeRecipientEmails(...lists) {
    const seen = new Set();
    const out = [];
    for (const list of lists) {
        for (const raw of list) {
            const email = raw.trim();
            const key = email.toLowerCase();
            if (!email || seen.has(key))
                continue;
            seen.add(key);
            out.push(email);
        }
    }
    return out;
}
/** Ticks an address on or off (compared without case). */
export function toggleRecipient(ticked, email) {
    const key = email.trim().toLowerCase();
    return ticked.some((e) => e.trim().toLowerCase() === key) ? ticked.filter((e) => e.trim().toLowerCase() !== key) : [...ticked, email];
}
/** Whether an address is ticked (compared without case). */
export function isRecipientTicked(ticked, email) {
    const key = email.trim().toLowerCase();
    return ticked.some((e) => e.trim().toLowerCase() === key);
}
